import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiErrorDetail, formatINR, formatDateTime, formatDate, istDate, istMonth, istYear } from "@/lib/api";
import { LayoutDashboard, PlusCircle, ListChecks, CheckCircle2, AlertTriangle, Clock, Loader2, CalendarDays, RefreshCw, Send, Pencil, Save, X, Filter, Trash2, KeyRound, MapPin, Banknote } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";
import MonthSelect from "@/components/MonthSelect";
import ConfirmDialog from "@/components/ConfirmDialog";

const NAV = [
  { key: "home", to: "/employee", label: "Overview", icon: LayoutDashboard, group: "Workspace" },
  { key: "donations", to: "/employee/donations", label: "Donations", icon: ListChecks, group: "Workspace" },
  { key: "clashes", to: "/employee/clashes", label: "Clashes", icon: AlertTriangle, group: "Workspace" },
  { key: "rechurn", to: "/employee/rechurn", label: "Rechurn Sheet", icon: RefreshCw, group: "Workspace" },
  { key: "payroll", to: "/employee/payroll", label: "Payroll", icon: Banknote, group: "My work" },
  { key: "attendance", to: "/employee/attendance", label: "Attendance", icon: CalendarDays, group: "My work" },
  { key: "leaves", to: "/employee/leaves", label: "Leaves", icon: CalendarDays, group: "My work" },
  { key: "password", to: "/employee/password", label: "Password", icon: KeyRound, group: "Account" },
];
const YEAR_OPTIONS = Array.from({ length: 21 }, (_, index) => String(new Date().getFullYear() - 10 + index));

const DEFAULT_SOURCES = ["upi", "web", "bank_acc", "gpay", "upi_id"];
const DEFAULT_ORGS = ["ccm", "ketto", "sbf", "bof"];
const ORGS = ["ccm", "ketto", "sbf", "bof"]; // fallback
export default function EmployeeDashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const page = employeePageFromPath(location.pathname);
  const [savedDonationView] = useState(readDonationViewState);
  const [dash, setDash] = useState(null);
  const [donations, setDonations] = useState([]);
  const [rechurn, setRechurn] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [donationRange, setDonationRange] = useState(savedDonationView.range || "all");
  const [donationDay, setDonationDay] = useState(savedDonationView.day || istDate());
  const [donationMonth, setDonationMonth] = useState(savedDonationView.month || istMonth());
  const [donationYear, setDonationYear] = useState(savedDonationView.year || istYear());
  const [donationSort, setDonationSort] = useState(savedDonationView.sort || "date_desc");
  const [donationStatus, setDonationStatus] = useState(savedDonationView.status || "all");
  const [donationSourceFilter, setDonationSourceFilter] = useState(savedDonationView.source || "");
  const [donationOrgFilter, setDonationOrgFilter] = useState(savedDonationView.organization || "");
  const [donationMinAmount, setDonationMinAmount] = useState(savedDonationView.minAmount || "");
  const [donationMaxAmount, setDonationMaxAmount] = useState(savedDonationView.maxAmount || "");
  const [donationPage, setDonationPage] = useState(Number(savedDonationView.page) || 1);
  const [donationPagination, setDonationPagination] = useState({ total: 0, pages: 1, total_amount: 0 });
  const [debouncedDonationSearch, setDebouncedDonationSearch] = useState(savedDonationView.search || "");
  const donationFiltersInitialized = useRef(false);
  const [form, setForm] = useState(() => {
    const fallback = { donor_name: "", donor_phone: "", amount: "", donation_date: istDate(), source: "upi", organization: "ccm", donor_email: "", transaction_ref: "", notes: "" };
    try { return { ...fallback, ...JSON.parse(localStorage.getItem("kaya:donation-draft") || "{}"), donation_date: istDate() }; }
    catch (_) { return fallback; }
  });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [leaveForm, setLeaveForm] = useState({ date: "", leave_type: "full_day", reason: "" });
  const [sources, setSources] = useState(DEFAULT_SOURCES);
  const [orgs, setOrgs] = useState(DEFAULT_ORGS);
  const [history, setHistory] = useState([]);
  const [attendanceMonth, setAttendanceMonth] = useState(istMonth());
  const [attendanceCalendar, setAttendanceCalendar] = useState([]);
  const [markingAttendance, setMarkingAttendance] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [donationSearch, setDonationSearch] = useState(savedDonationView.search || "");
  const [pw, setPw] = useState({ current_password: "", password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [peers, setPeers] = useState([]);
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  useEffect(() => {
    api.get("/constants").then(r => {
      if (r.data.sources?.length) setSources(r.data.sources);
      if (r.data.organizations?.length) setOrgs(r.data.organizations);
    }).catch(() => {});
    api.get("/employee/salary-history?months=6")
      .then(r => setHistory(r.data || []))
      .catch(() => {});
    api.get("/employee/peers")
      .then(r => setPeers(r.data || []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    localStorage.setItem("kaya:donation-draft", JSON.stringify(form));
  }, [form]);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedDonationSearch(donationSearch.trim()), 300);
    return () => clearTimeout(timer);
  }, [donationSearch]);
  useEffect(() => {
    localStorage.setItem("kaya:employee-donation-view", JSON.stringify({
      range: donationRange, day: donationDay, month: donationMonth, year: donationYear,
      sort: donationSort, status: donationStatus, source: donationSourceFilter,
      organization: donationOrgFilter, minAmount: donationMinAmount, maxAmount: donationMaxAmount,
      search: donationSearch, page: donationPage,
    }));
  }, [donationRange, donationDay, donationMonth, donationYear, donationSort, donationStatus,
    donationSourceFilter, donationOrgFilter, donationMinAmount, donationMaxAmount, donationSearch, donationPage]);
  useEffect(() => {
    if (!donationFiltersInitialized.current) {
      donationFiltersInitialized.current = true;
      return;
    }
    setDonationPage(1);
  }, [donationRange, donationDay, donationMonth, donationYear,
    debouncedDonationSearch, donationStatus, donationSourceFilter, donationOrgFilter, donationMinAmount, donationMaxAmount, donationSort]);

  const load = async () => {
    try {
      const donationsQuery = donationListQuery(donationRange, donationDay, donationMonth, donationYear, {
        paginated: page === "donations", page: donationPage, pageSize: 50,
        search: debouncedDonationSearch, status: donationStatus,
        source: donationSourceFilter, organization: donationOrgFilter,
        minAmount: donationMinAmount, maxAmount: donationMaxAmount, sort: donationSort,
      });
      const [d, l1, l2, lv, cal] = await Promise.all([
        api.get("/employee/dashboard"),
        api.get(donationsQuery),
        api.get("/donations?only_stale=true"),
        api.get("/leaves"),
        api.get(`/employee/attendance/calendar?month=${attendanceMonth}`),
      ]);
      setDash(d.data);
      if (l1.data?.items) {
        setDonations(l1.data.items);
        setDonationPagination({ total: l1.data.total || 0, pages: l1.data.pages || 1, total_amount: l1.data.total_amount || 0 });
      } else {
        setDonations(l1.data || []);
      }
      setRechurn(l2.data); setLeaves(lv.data);
      setAttendanceCalendar(cal.data.days || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, donationRange, donationDay, donationMonth, donationYear, attendanceMonth, donationPage,
      debouncedDonationSearch, donationStatus, donationSourceFilter, donationOrgFilter, donationMinAmount, donationMaxAmount, donationSort]);
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.donor_name || !form.donor_phone || !form.amount) { toast.error("Fill required fields"); return; }
    if (form.share_enabled && !form.share_employee_id) { toast.error("Select employee to share with"); return; }
    setSaving(true);
    try {
      const amount = parseFloat(form.amount);
      const repeatedDonation = await shouldMarkRepeatedDonation(form.donor_phone, amount, form.donation_date);
      if (repeatedDonation === null) return;
      await saveDonationPayload(repeatedDonation, amount);
      localStorage.removeItem("kaya:donation-draft");
      setForm({ ...form, donor_name: "", donor_phone: "", amount: "", donation_date: istDate(), donor_email: "", transaction_ref: "", notes: "", share_enabled: false, share_employee_id: "" });
      toast.success(repeatedDonation ? "Repeated donation recorded" : "Donation recorded");
      load();
    } catch (e) {
      const duplicateDetail = duplicateDonationDetail(e);
      if (duplicateDetail) {
        const amount = parseFloat(form.amount);
        openDuplicatePrompt(
          duplicateDetail.donation || {},
          amount,
          duplicateDetail.code === "cross_employee_duplicate" ? "cross_employee" : "same_employee",
        );
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    }
    finally { setSaving(false); }
  };

  const saveDonationPayload = (repeatedDonation, amount) => api.post("/donations", {
    ...form,
    amount,
    share_employee_id: form.share_enabled ? form.share_employee_id : null,
    repeated_donation: repeatedDonation,
    allow_clash_duplicate: false,
  });

  const shouldMarkRepeatedDonation = async (phone, amount, date) => {
    const params = new URLSearchParams({
      donor_phone: normalizePhone(phone),
      amount: String(amount),
      donation_date: date,
    });
    const { data } = await api.get(`/donations/duplicate-check?${params.toString()}`);
    if (data?.duplicate) {
      openDuplicatePrompt(data.donation || {}, amount, "same_employee");
      return null;
    }
    if (data?.cross_employee_duplicate) {
      openDuplicatePrompt(data.cross_employee_donation || {}, amount, "cross_employee");
      return null;
    }
    return false;
  };

  const openDuplicatePrompt = (duplicate, amount, type = "same_employee") => {
    setDuplicatePrompt({
      duplicate,
      amount,
      type,
      formSnapshot: { ...form },
    });
    return null;
  };

  const cancelRepeatedDonation = () => setDuplicatePrompt(null);

  const confirmRepeatedDonation = async () => {
    if (!duplicatePrompt) return;
    const snapshot = duplicatePrompt.formSnapshot;
    setSaving(true);
    try {
      await api.post("/donations", {
        ...snapshot,
        amount: duplicatePrompt.amount,
        share_employee_id: snapshot.share_enabled ? snapshot.share_employee_id : null,
        repeated_donation: duplicatePrompt.type === "same_employee",
        allow_clash_duplicate: duplicatePrompt.type === "cross_employee",
      });
      setDuplicatePrompt(null);
      localStorage.removeItem("kaya:donation-draft");
      setForm({ ...snapshot, donor_name: "", donor_phone: "", amount: "", donation_date: istDate(), donor_email: "", transaction_ref: "", notes: "", share_enabled: false, share_employee_id: "" });
      toast.success(duplicatePrompt.type === "cross_employee" ? "Donation saved as clash" : "Repeated donation recorded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/donations/${editing.id}`, { ...editing, amount: parseFloat(editing.amount) });
      toast.success("Updated"); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const applyLeave = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leaves", leaveForm);
      toast.success("Leave applied — awaiting approval");
      setLeaveForm({ date: "", leave_type: "full_day", reason: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const deleteLeave = async (leave) => {
    try {
      await api.delete(`/leaves/${leave.id}`);
      toast.success("Leave deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw.password !== pw.confirm) { toast.error("Passwords do not match"); return; }
    setSavingPw(true);
    try {
      await api.patch("/auth/me", { current_password: pw.current_password, password: pw.password });
      toast.success("Password updated");
      setPw({ current_password: "", password: "", confirm: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingPw(false); }
  };

  const locationErrorMessage = (error) => {
    if (error?.code === 1) return "Location permission is blocked. Please turn on location permission for this site in browser settings, then click Clock in again.";
    if (error?.code === 2) return "Location is not available. Please turn on GPS/location and internet, then click Clock in again.";
    if (error?.code === 3) return "Location request timed out. Please keep location on and click Clock in again.";
    return error?.message || "Location is required to clock in. Please turn on location and try again.";
  };

  const getClockInLocation = async () => {
    if (!navigator.geolocation) {
      throw new Error("Location is not supported in this browser");
    }
    if (navigator.permissions?.query) {
      try {
        const permission = await navigator.permissions.query({ name: "geolocation" });
        if (permission.state === "denied") {
          throw new Error("Location permission is blocked. Please turn on location permission for this site in browser settings, then click Clock in again.");
        }
      } catch (e) {
        if (String(e.message || "").includes("Location permission is blocked")) throw e;
      }
    }
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        }),
        (error) => reject(new Error(locationErrorMessage(error))),
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
      );
    });
  };

  const markAttendance = async () => {
    setMarkingAttendance(true);
    try {
      const location = await getClockInLocation();
      await api.post("/employee/attendance/mark", { location });
      toast.success("Checked in");
      load();
    } catch (e) {
      const apiDetail = e.response?.data?.detail;
      toast.error(apiDetail != null ? formatApiErrorDetail(apiDetail) : locationErrorMessage(e));
    }
    finally { setMarkingAttendance(false); }
  };

  const checkoutAttendance = async () => {
    setCheckingOut(true);
    try {
      await api.post("/employee/attendance/checkout");
      toast.success("Checked out");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setCheckingOut(false); }
  };

  const runConfirmAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (action?.type === "leave") await deleteLeave(action.leave);
    if (action?.type === "checkout") await checkoutAttendance();
  };

  const att = dash?.attendance_today;
  const salary = dash?.salary;
  const stMap = {
    full_day: { label: "Full Day", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: CheckCircle2 },
    late: { label: "Late", cls: "text-amber-700 bg-amber-50 border-amber-200", Icon: Clock },
    half_day: { label: "Half Day", cls: "text-orange-700 bg-orange-50 border-orange-200", Icon: AlertTriangle },
    on_leave: { label: "On leave", cls: "text-violet-700 bg-violet-50 border-violet-200", Icon: CalendarDays },
  };
  const si = att ? (stMap[att.leave_id ? "on_leave" : att.status] || stMap.full_day) : null;
  const today = istDate();
  const otherLeaves = leaves
    .filter((l) => !(l.status === "approved" && String(l.date || "") <= today))
    .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
  const filteredDonations = donations.filter((d) => {
    const q = donationSearch.trim().toLowerCase();
    if (donationStatus === "verified" && !d.approved) return false;
    if (donationStatus === "unverified" && d.approved) return false;
    if (donationStatus === "clash" && !d.clash) return false;
    if (donationStatus === "repeated" && !d.repeated_donation) return false;
    if (donationStatus === "shared" && !d.shared_group_id) return false;
    if (donationStatus === "rejected" && !d.rejected) return false;
    if (donationSourceFilter && d.source !== donationSourceFilter) return false;
    if (donationOrgFilter && d.organization !== donationOrgFilter) return false;
    if (!q) return true;
    const date = String(d.donation_date || d.created_at || "").slice(0, 10);
    return [d.donor_name, d.donor_phone, date].some((v) => String(v || "").toLowerCase().includes(q));
  }).sort((a, b) => compareDonations(a, b, donationSort));
  const filteredDonationTotal = filteredDonations.reduce((sum, d) => sum + Number(d.amount || 0), 0);
  const donationFilterCount = [donationSearch, donationStatus !== "all" ? donationStatus : "", donationSourceFilter, donationOrgFilter, donationMinAmount, donationMaxAmount].filter((value) => value !== "").length;
  const clearDonationFilters = () => { setDonationSearch(""); setDonationStatus("all"); setDonationSourceFilter(""); setDonationOrgFilter(""); setDonationMinAmount(""); setDonationMaxAmount(""); setDonationSort("date_desc"); };
  const filteredClashes = donations.filter((d) => {
    if (!d.clash) return false;
    if (donationSourceFilter && d.source !== donationSourceFilter) return false;
    if (donationOrgFilter && d.organization !== donationOrgFilter) return false;
    const q = donationSearch.trim().toLowerCase();
    if (!q) return true;
    const date = String(d.donation_date || d.created_at || "").slice(0, 10);
    return [d.donor_name, d.donor_phone, d.employee_name, date].some((v) => String(v || "").toLowerCase().includes(q));
  }).sort((a, b) => compareDonations(a, b, donationSort));

  return (
    <AppShell title={`Namaste, ${user?.name?.split(" ")[0] || "team"}`} subtitle="Track collections, target progress and attendance." nav={NAV}>
      {!dash ? (<div className="flex py-24 text-[#5C635E]"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>) : (
        <>
        {page === "overview" && (
        <>
        <div id="emp-stats" className="scroll-mt-24 grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Kpi label="Today's Collection" value={formatINR(dash.daily_amount)} sub={`${dash.daily_count} donations`} />
          <Kpi label="Achieved (Month)" value={formatINR(salary.achieved)} sub={`Target ${formatINR(salary.target)}`} accent />
          <Kpi label="Remaining Target" value={formatINR(salary.remaining_target)} sub={salary.target > 0 ? `${Math.round((salary.achieved / salary.target) * 100)}% done` : "—"} />
          <div className="card-warm p-5" data-testid="attendance-card">
            <div className="text-overline">Today</div>
            {att && si ? (
              <>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium ${si.cls}`} data-testid="attendance-status"><si.Icon className="h-3.5 w-3.5" />{si.label}</div>
                  {att.office_location_ok && (
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium text-emerald-700 bg-emerald-50 border-emerald-200" data-testid="office-location-ok">
                      <MapPin className="h-3.5 w-3.5" />
                      In office
                    </div>
                  )}
                </div>
                {att.leave_id ? <div className="mt-2 text-xs text-violet-700">{att.leave_type === "half_day" ? "Half-day" : "Full-day"} leave · {att.leave_status || "applied"}</div> : <>
                <div className="mt-2 text-xs text-[#5C635E]">Clock in: {formatDateTime(att.marked_at || att.login_time)}</div>
                {att.logout_time && <div className="mt-1 text-xs text-[#5C635E]">Clock out: {formatDateTime(att.logout_time)}</div>}
                {att.logout_time ? (
                  <button
                    data-testid="clock-in-button"
                    type="button"
                    onClick={markAttendance}
                    disabled={markingAttendance}
                    className="mt-3 bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-xl px-4 py-2 text-sm flex items-center gap-2"
                  >
                    {markingAttendance ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    {markingAttendance ? "Clocking in..." : "Clock in"}
                  </button>
                ) : (
                  <button
                    data-testid="clock-out-button"
                    type="button"
                    onClick={() => setConfirmAction({ type: "checkout" })}
                    disabled={checkingOut}
                    className="mt-3 bg-[#C05746] hover:bg-[#A9483A] disabled:opacity-60 text-white rounded-xl px-4 py-2 text-sm flex items-center gap-2"
                  >
                    {checkingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
                    {checkingOut ? "Clocking out..." : "Clock out"}
                  </button>
                )}</>}
              </>
            ) : (
              <>
                <div className="text-sm text-[#8A938D] mt-2">Not clocked in today.</div>
                <button
                  data-testid="clock-in-button"
                  type="button"
                  onClick={markAttendance}
                  disabled={markingAttendance}
                  className="mt-3 bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-xl px-4 py-2 text-sm flex items-center gap-2"
                >
                  {markingAttendance ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {markingAttendance ? "Clocking in..." : "Clock in"}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="scroll-mt-24 card-warm p-5 mb-6" data-testid="employee-today-incentives">
          <div className="text-overline">Today</div>
          <h3 className="text-lg font-medium text-[#2D312E] mt-1" style={{ fontFamily: "Manrope" }}>Available incentives</h3>
          <IncentiveRules incentives={dash.today_incentives} />
        </div>

        <div id="emp-pending" className="scroll-mt-24 card-warm p-5 mb-6 border-l-4 border-l-amber-400" data-testid="pending-settlement">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-overline text-amber-700">Pending Settlement</div>
              <div className="text-2xl mt-1 text-[#2D312E]" style={{ fontFamily: "Manrope" }}>
                {formatINR(dash.pending_settlement_amount || 0)}
                <span className="text-sm text-[#8A938D] font-normal ml-2">({dash.pending_settlement_count || 0} donations)</span>
              </div>
            </div>
            <div className="text-xs text-[#8A938D] max-w-sm">
              Donations you logged that are not yet verified in the Google Sheet. Verified donations will move out of this list automatically.
            </div>
          </div>
        </div>
        </>
        )}

        {page === "payroll" && (
        <>
        <div id="emp-salary" className="scroll-mt-24 grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="salary-panel">
          <SmallStat label="Present" value={salary.attendance.present} />
          <SmallStat label="Absent" value={salary.attendance.absent} danger />
          <SmallStat label="Half Days" value={salary.attendance.half_day} />
          <SmallStat label="Late Marks" value={`${salary.attendance.late} (${salary.attendance.late_to_absent} absent)`} />
          <SmallStat label="Basic Salary" value={formatINR(salary.basic_salary)} />
          <SmallStat label="Daily Wage" value={formatINR(salary.daily_wage)} sub={`${salary.deduction_breakdown?.calendar_days_in_month || ""} days`} />
          <SmallStat label="Deductions" value={formatINR(salary.absent_deduction)} danger />
          <SmallStat label="Salary Till Date" value={formatINR(salary.salary_till_date)} sub={`${salary.elapsed_calendar_days || 0} calendar days`} accent />
          <SmallStat label="Full-Month Net Pay" value={formatINR(salary.total_salary)} sub="Basic + incentives − deductions" accent />
        </div>

        <PayrollDetail salary={salary} />
        </>
        )}

        {/* Add donation */}
        {page === "donations" && (
        <form id="emp-add" onSubmit={onSubmit} className="scroll-mt-24 card-warm p-4 sm:p-6 mb-5 sm:mb-8 pb-24 sm:pb-6" data-testid="add-donation-form">
          <div className="text-overline">Record</div>
          <h3 className="text-xl font-medium text-[#2D312E] mt-1 mb-5" style={{ fontFamily: "Manrope" }}>Add donation</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <F label="Date*" testid="d-date" type="date" value={form.donation_date} onChange={(v) => setForm({ ...form, donation_date: v })} />
            <F label="Donor Name*" testid="d-name" value={form.donor_name} onChange={(v) => setForm({ ...form, donor_name: v })} />
            <F label="Mobile*" testid="d-phone" type="tel" inputMode="tel" value={form.donor_phone} onChange={(v) => setForm({ ...form, donor_phone: v })} />
            <F label="Amount (₹)*" testid="d-amount" type="number" inputMode="decimal" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} />
            <FSelect label="Source*" testid="d-source" value={form.source} onChange={(v) => setForm({ ...form, source: v })} options={sources} />
            <FSelect label="Organization*" testid="d-org" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} options={orgs} />
            <F label="Email" testid="d-email" value={form.donor_email} onChange={(v) => setForm({ ...form, donor_email: v })} />
            <F label="Transaction Ref" testid="d-txn" value={form.transaction_ref} onChange={(v) => setForm({ ...form, transaction_ref: v })} placeholder="UTR / txn id (optional)" />
            <div>
              <label className="text-sm font-medium text-[#5C635E] mb-1.5 block">Share donation</label>
              <label className="h-[46px] flex items-center gap-2 bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-4">
                <input
                  data-testid="d-share-enabled"
                  type="checkbox"
                  checked={!!form.share_enabled}
                  onChange={(e) => setForm({ ...form, share_enabled: e.target.checked, share_employee_id: e.target.checked ? (form.share_employee_id || peers[0]?.id || "") : "" })}
                />
                <span className="text-sm text-[#5C635E]">Split with employee</span>
              </label>
            </div>
            {form.share_enabled && (
              <FSelect
                label="Share with*"
                testid="d-share-employee"
                value={form.share_employee_id || ""}
                onChange={(v) => setForm({ ...form, share_employee_id: v })}
                options={peers.map((p) => ({ value: p.id, label: p.name }))}
              />
            )}
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-[#5C635E] mb-1.5 block">Notes</label>
              <input data-testid="d-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-4 py-2.5" />
            </div>
          </div>
          <div className="mt-5">
            <button data-testid="save-donation-button" type="submit" disabled={saving} className="w-full sm:w-auto min-h-12 justify-center shadow-lg sm:shadow-none bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-xl px-6 py-2.5 text-sm flex items-center gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
              {saving ? "Saving…" : "Save donation"}
            </button>
          </div>
        </form>
        )}

        {/* My donations */}
        {page === "donations" && (
        <div id="emp-list" className="scroll-mt-24 card-warm p-4 sm:p-6 mb-8" data-testid="my-donations">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="text-overline">{donationRange === "all" ? "All records" : donationRange === "day" ? donationDay : donationRange === "month" ? donationMonth : donationYear}</div>
              <h3 className="text-xl font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>All donations</h3>
            </div>
            <div className="flex gap-2 text-sm items-center flex-wrap w-full lg:w-auto" data-testid="period-filter">
              <div className="relative w-full sm:w-auto">
                <Filter className="h-4 w-4 text-[#8A938D] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  data-testid="donation-search"
                  value={donationSearch}
                  onChange={(e) => setDonationSearch(e.target.value)}
                  placeholder="Search name, number, date"
                  className="w-full sm:w-auto bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg pl-9 pr-3 py-2.5 sm:py-1.5 sm:min-w-[260px]"
                />
              </div>
              <div className="flex gap-1.5 overflow-x-auto max-w-full pb-1">
              {["all","day","week","month","year"].map(p => (
                <button type="button" key={p} data-testid={`period-${p}`} onClick={() => setDonationRange(p)} className={`min-h-10 shrink-0 px-3 py-1 rounded-lg capitalize ${donationRange===p?"bg-[#4A5D4E] text-white":"border border-[#EAE6DF] text-[#5C635E]"}`}>{p}</button>
              ))}
              </div>
              {donationRange === "day" && (
                <input
                  data-testid="donation-day"
                  aria-label="Donation date"
                  type="date"
                  value={donationDay}
                  onChange={(e) => setDonationDay(e.target.value)}
                  className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-1.5"
                />
              )}
              {donationRange === "month" && (
                <MonthSelect testId="donation-month" value={donationMonth} onChange={setDonationMonth} />
              )}
              {donationRange === "year" && (
                <select
                  data-testid="donation-year"
                  aria-label="Donation year"
                  value={donationYear}
                  onChange={(e) => setDonationYear(e.target.value)}
                  className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 w-28"
                >{YEAR_OPTIONS.map((year) => <option key={year} value={year}>{year}</option>)}</select>
              )}
              <select
                data-testid="donation-sort"
                aria-label="Sort donations"
                value={donationSort}
                onChange={(e) => setDonationSort(e.target.value)}
                className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-1.5"
              >
                <option value="date_desc">Newest first</option>
                <option value="date_asc">Oldest first</option>
                <option value="amount_desc">Amount high to low</option>
                <option value="amount_asc">Amount low to high</option>
                <option value="name_asc">Donor name A-Z</option>
                <option value="status">Status priority</option>
              </select>
              {donationFilterCount > 0 && <button type="button" data-testid="employee-clear-donation-filters" onClick={clearDonationFilters} className="min-h-10 px-3 rounded-lg border border-[#EAE6DF] text-[#C05746]">Clear ({donationFilterCount})</button>}
              <select
                data-testid="donation-status-filter"
                aria-label="Donation status"
                value={donationStatus}
                onChange={(e) => setDonationStatus(e.target.value)}
                className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-1.5"
              >
                <option value="all">All status</option>
                <option value="verified">Verified</option>
                <option value="unverified">Unverified</option>
                <option value="clash">Clash</option>
                <option value="repeated">Repeated</option>
                <option value="shared">Shared</option>
                <option value="rejected">Rejected</option>
              </select>
              <input aria-label="Minimum donation amount" type="number" min="0" inputMode="decimal" value={donationMinAmount}
                onWheel={(e) => e.currentTarget.blur()} onChange={(e) => setDonationMinAmount(e.target.value)} placeholder="Min ₹"
                className="w-28 bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2" />
              <input aria-label="Maximum donation amount" type="number" min="0" inputMode="decimal" value={donationMaxAmount}
                onWheel={(e) => e.currentTarget.blur()} onChange={(e) => setDonationMaxAmount(e.target.value)} placeholder="Max ₹"
                className="w-28 bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2" />
              <select
                data-testid="donation-source-filter"
                aria-label="Donation source"
                value={donationSourceFilter}
                onChange={(e) => setDonationSourceFilter(e.target.value)}
                className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-1.5 uppercase"
              >
                <option value="">All sources</option>
                {sources.map((src) => <option key={src} value={src}>{String(src).toUpperCase()}</option>)}
              </select>
              <select
                data-testid="donation-org-filter"
                aria-label="Donation organization"
                value={donationOrgFilter}
                onChange={(e) => setDonationOrgFilter(e.target.value)}
                className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-1.5 uppercase"
              >
                <option value="">All orgs</option>
                {orgs.map((org) => <option key={org} value={org}>{String(org).toUpperCase()}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="employee-donation-total">
            <SmallStat label="Selected collection" value={formatINR(donationPagination.total_amount || filteredDonationTotal)} accent />
            <SmallStat label="Donations" value={donationPagination.total || filteredDonations.length} />
          </div>
          <DonationTable items={filteredDonations} editing={editing} setEditing={setEditing} onSave={saveEdit} highlightStale sources={sources} orgs={orgs} />
          {donationPagination.pages > 1 && <div className="mt-4 flex items-center justify-between gap-3" data-testid="employee-donation-pagination">
            <button type="button" disabled={donationPage <= 1} onClick={() => setDonationPage((value) => Math.max(1, value - 1))} className="min-h-11 border border-[#EAE6DF] rounded-xl px-4 text-sm disabled:opacity-40">Previous</button>
            <span className="text-sm text-[#5C635E]">Page {donationPage} of {donationPagination.pages}</span>
            <button type="button" disabled={donationPage >= donationPagination.pages} onClick={() => setDonationPage((value) => value + 1)} className="min-h-11 border border-[#EAE6DF] rounded-xl px-4 text-sm disabled:opacity-40">Next</button>
          </div>}
        </div>
        )}

        {page === "clashes" && (
        <div id="emp-clashes" className="scroll-mt-24 card-warm p-6 mb-8" data-testid="employee-clashes">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="text-overline text-[#C05746]">Verification</div>
              <h3 className="text-xl font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Clashed donations</h3>
            </div>
            <div className="flex gap-2 text-sm items-center flex-wrap" data-testid="clash-filter">
              <div className="relative">
                <Filter className="h-4 w-4 text-[#8A938D] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  data-testid="clash-search"
                  value={donationSearch}
                  onChange={(e) => setDonationSearch(e.target.value)}
                  placeholder="Search name, number, date"
                  className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg pl-9 pr-3 py-1.5 min-w-[260px]"
                />
              </div>
              {["all","day","month","year"].map(p => (
                <button key={p} data-testid={`clash-period-${p}`} onClick={() => setDonationRange(p)} className={`px-3 py-1 rounded-lg capitalize ${donationRange===p?"bg-[#4A5D4E] text-white":"border border-[#EAE6DF] text-[#5C635E]"}`}>{p}</button>
              ))}
              {donationRange === "day" && (
                <input type="date" value={donationDay} onChange={(e) => setDonationDay(e.target.value)}
                  className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-1.5" />
              )}
              {donationRange === "month" && (
                <MonthSelect value={donationMonth} onChange={setDonationMonth} />
              )}
              {donationRange === "year" && (
                <select aria-label="Donation year" value={donationYear} onChange={(e) => setDonationYear(e.target.value)}
                  className="bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 w-28">{YEAR_OPTIONS.map((year) => <option key={year} value={year}>{year}</option>)}</select>
              )}
            </div>
          </div>
          <DonationTable items={filteredClashes} editing={editing} setEditing={setEditing} onSave={saveEdit} highlightStale sources={sources} orgs={orgs} groupClashes />
        </div>
        )}

        {page === "attendance" && (
        <div id="emp-attendance" className="scroll-mt-24 card-warm p-5 mb-8" data-testid="attendance-calendar">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="text-overline">Attendance</div>
              <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Monthly calendar</h3>
            </div>
            <MonthSelect value={attendanceMonth} onChange={setAttendanceMonth} />
          </div>
          <div className="grid grid-cols-7 gap-2 text-center text-xs">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d} className="text-[#8A938D] font-semibold">{d}</div>)}
            {Array.from({ length: (new Date(`${attendanceMonth}-01T00:00:00`).getDay() + 6) % 7 }).map((_, i) => (
              <div key={`blank-${i}`} />
            ))}
            {attendanceCalendar.map((d) => (
              <div key={d.date} className={`rounded-lg border px-1 py-2 min-h-[54px] ${
                d.status === "full_day" ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
                d.status === "late" ? "bg-amber-50 border-amber-200 text-amber-800" :
                d.status === "half_day" ? "bg-orange-50 border-orange-200 text-orange-800" :
                d.status === "on_leave" ? "bg-violet-50 border-violet-200 text-violet-800" :
                d.status === "absent" ? "bg-red-50 border-red-200 text-red-800" :
                d.status === "holiday" ? "bg-[#F4F1EA] border-[#EAE6DF] text-[#8A938D]" :
                "bg-white border-[#EAE6DF] text-[#8A938D]"
              }`}>
                <div className="font-semibold">{Number(d.date.slice(8, 10))}</div>
                <div className="mt-1 capitalize">{String(d.status || "").replace("_", " ")}</div>
              </div>
            ))}
          </div>
        </div>
        )}
        {page === "payroll" && (
        <>
        {/* Incentive breakdown */}
        {salary.incentive_breakdown && !salary.incentive_breakdown.manual_only && (
          <div className="card-warm p-5 mb-8" data-testid="incentive-breakdown">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="text-overline">Monthly incentive</div>
                <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Cumulative stacked incentives</h3>
                <p className="text-xs text-[#8A938D] mt-1">Daily + Weekly + Monthly slabs stack together, plus any manual override.</p>
              </div>
              <div className="text-right">
                <div className="text-xs text-[#8A938D] uppercase tracking-widest">Total</div>
                <div className="text-2xl font-medium text-[#4A5D4E]" style={{ fontFamily: "Manrope" }} data-testid="incentive-total">{formatINR(salary.incentive_breakdown.total)}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <BreakdownPill label="Daily" value={formatINR(salary.incentive_breakdown.daily)} testid="bd-daily" />
              <BreakdownPill label="Hourly" value={formatINR(salary.incentive_breakdown.hourly)} testid="bd-hourly" />
              <BreakdownPill label="Weekly" value={formatINR(salary.incentive_breakdown.weekly)} testid="bd-weekly" />
              <BreakdownPill label="Monthly" value={formatINR(salary.incentive_breakdown.monthly)} testid="bd-monthly" />
              <BreakdownPill label="Manual" value={formatINR(salary.incentive_breakdown.manual)} testid="bd-manual" />
            </div>
            {(salary.incentive_breakdown.rule_breakdown || []).length > 0 && (
              <div className="mt-4 border border-[#EAE6DF] rounded-xl bg-[#FDFBF7] divide-y divide-[#EAE6DF]" data-testid="employee-incentive-sources">
                {salary.incentive_breakdown.rule_breakdown.map((item) => (
                  <div key={`${item.rule_id}-${item.type}`} className="p-3 flex items-center justify-between gap-3 text-sm">
                    <div>
                      <div className="font-medium text-[#2D312E]">{item.name}</div>
                      <div className="text-xs text-[#8A938D] capitalize">{item.type} - {item.period} - {item.donation_count} donations</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-[#8A938D]">{formatINR(item.collection)} collection</div>
                      <div className="font-medium text-[#4A5D4E]">{formatINR(item.amount)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {salary.incentive_breakdown && salary.incentive_breakdown.manual_only && (
          <div className="card-warm p-5 mb-8" data-testid="incentive-breakdown">
            <div className="text-overline">Monthly incentive</div>
            <div className="flex items-center justify-between mt-2">
              <div className="text-sm text-[#5C635E]">Manual mode — admin has set a fixed amount.</div>
              <div className="text-2xl font-medium text-[#4A5D4E]" data-testid="incentive-total">{formatINR(salary.incentive_breakdown.total)}</div>
            </div>
          </div>
        )}

        {/* Salary history */}
        {history.length > 0 && (
          <div className="card-warm p-5 mb-8" data-testid="salary-history">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
              <div>
                <div className="text-overline">Past months</div>
                <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Salary history</h3>
                <p className="text-xs text-[#8A938D] mt-1">Each month starts a fresh incentive cycle. Past bonuses stay locked at the rules that were active on those days.</p>
              </div>
            </div>
            <div className="md:hidden space-y-3">
              {history.map((h) => <div key={h.month} className="rounded-2xl border border-[#EAE6DF] bg-white p-4"><div className="flex items-center justify-between"><span className="font-semibold text-[#2D312E]">{h.month}</span><span className="font-semibold text-[#4A5D4E]">{formatINR(h.net_pay)}</span></div><div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-xs"><span className="text-[#8A938D]">Basic</span><span className="text-right">{formatINR(h.basic_salary)}</span><span className="text-[#8A938D]">Daily wage</span><span className="text-right">{formatINR(h.daily_wage)}</span><span className="text-[#8A938D]">Incentives</span><span className="text-right text-emerald-700">+ {formatINR(h.monthly_incentive)}</span><span className="text-[#8A938D]">Deductions</span><span className="text-right text-[#C05746]">− {formatINR(h.absent_deduction)}</span></div></div>)}
            </div>
            <div className="hidden md:block overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
                    <th className="text-left p-3">Month</th>
                    <th className="text-right p-3">Basic</th>
                    <th className="text-right p-3">Daily wage</th>
                    <th className="text-right p-3">Incentives</th>
                    <th className="text-right p-3 text-[#C05746]">Deductions</th>
                    <th className="text-right p-3 font-semibold">Net pay</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.month} data-testid={`history-row-${h.month}`} className="border-t border-[#EAE6DF]">
                      <td className="p-3 font-medium text-[#2D312E]">{h.month}</td>
                      <td className="p-3 text-right text-[#5C635E]">{formatINR(h.basic_salary)}</td>
                      <td className="p-3 text-right text-[#5C635E]">{formatINR(h.daily_wage)}</td>
                      <td className="p-3 text-right text-[#5C635E]">{formatINR(h.monthly_incentive)}</td>
                      <td className="p-3 text-right text-[#C05746]">− {formatINR(h.absent_deduction)}</td>
                      <td className="p-3 text-right font-semibold text-[#4A5D4E]">{formatINR(h.net_pay)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
        )}

        {/* Rechurn */}
        {page === "rechurn" && (
        <div id="emp-rechurn" className="scroll-mt-24 card-warm p-4 sm:p-6 mb-8" data-testid="rechurn-sheet">
          <div className="text-overline">Rechurn</div>
          <h3 className="text-xl font-medium text-[#2D312E] mb-4" style={{ fontFamily: "Manrope" }}>Donors quiet for 3+ months</h3>
          <DonationTable items={rechurn} readOnly highlightStale />
        </div>
        )}

        {/* Leaves */}
        {page === "leaves" && (
        <div id="emp-leaves" className="scroll-mt-24 card-warm p-4 sm:p-6 mb-8" data-testid="leaves-section">
          <div className="text-overline">Leave</div>
          <h3 className="text-xl font-medium text-[#2D312E] mb-4" style={{ fontFamily: "Manrope" }}>Apply for leave</h3>
          <form onSubmit={applyLeave} className="flex gap-3 flex-wrap items-end mb-6" data-testid="leave-form">
            <F label="Date*" testid="leave-date" type="date" value={leaveForm.date} onChange={(v) => setLeaveForm({ ...leaveForm, date: v })} />
            <div className="min-w-[180px]">
              <label className="text-sm font-medium text-[#5C635E] mb-1.5 block">Leave type*</label>
              <select data-testid="leave-type" value={leaveForm.leave_type} onChange={(e) => setLeaveForm({ ...leaveForm, leave_type: e.target.value })} className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-4 py-2.5">
                <option value="full_day">Full day</option>
                <option value="half_day">Half day</option>
              </select>
            </div>
            <div className="flex-1 min-w-[240px]">
              <label className="text-sm font-medium text-[#5C635E] mb-1.5 block">Reason*</label>
              <input data-testid="leave-reason" required value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-4 py-2.5" />
            </div>
            <button data-testid="submit-leave" type="submit" className="w-full sm:w-auto min-h-12 justify-center bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-xl px-5 py-2.5 text-sm flex items-center gap-2"><Send className="h-4 w-4" />Apply</button>
          </form>
          <div className="w-full">
            <LeaveList title="Requests" empty="No pending or upcoming leaves." items={otherLeaves} onDelete={(leave) => setConfirmAction({ type: "leave", leave })} />
          </div>
        </div>
        )}
        {page === "password" && (
        <form id="employee-password" onSubmit={changePassword} className="scroll-mt-24 card-warm p-4 sm:p-6" data-testid="employee-password">
          <div className="text-overline">Security</div>
          <h3 className="text-xl font-medium text-[#2D312E] mb-4" style={{ fontFamily: "Manrope" }}>Change password</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <F label="Current password" testid="employee-current-password" type="password" value={pw.current_password} onChange={(v) => setPw({ ...pw, current_password: v })} />
            <F label="New password" testid="employee-new-password" type="password" value={pw.password} onChange={(v) => setPw({ ...pw, password: v })} />
            <F label="Confirm new password" testid="employee-confirm-password" type="password" value={pw.confirm} onChange={(v) => setPw({ ...pw, confirm: v })} />
          </div>
          <button data-testid="employee-save-password" type="submit" disabled={savingPw}
            className="mt-5 w-full sm:w-auto min-h-12 justify-center bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-xl px-6 py-2.5 text-sm flex items-center gap-2">
            {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            {savingPw ? "Saving..." : "Change password"}
          </button>
        </form>
        )}
        <ConfirmDialog
          open={!!confirmAction}
          title={confirmAction?.type === "checkout" ? "Clock out now?" : "Delete leave request?"}
          message={confirmAction?.type === "checkout" ? "Are you sure you want to clock out now?" : `Delete leave request for ${confirmAction?.leave?.date}?`}
          confirmLabel={confirmAction?.type === "checkout" ? "Clock out" : "Delete"}
          onConfirm={runConfirmAction}
          onCancel={() => setConfirmAction(null)}
        />
        {duplicatePrompt && (
          <DuplicateDonationModal
            duplicate={duplicatePrompt.duplicate}
            amount={duplicatePrompt.amount}
            date={duplicatePrompt.formSnapshot?.donation_date}
            type={duplicatePrompt.type}
            saving={saving}
            onCancel={cancelRepeatedDonation}
            onConfirm={confirmRepeatedDonation}
          />
        )}
        </>
      )}
    </AppShell>
  );
}

function employeePageFromPath(pathname) {
  const part = String(pathname || "").split("/")[2] || "overview";
  return ["donations", "clashes", "payroll", "attendance", "rechurn", "leaves", "password"].includes(part) ? part : "overview";
}

function readDonationViewState() {
  try { return JSON.parse(localStorage.getItem("kaya:employee-donation-view") || "{}"); }
  catch (_) { return {}; }
}

function donationListQuery(range, day, month, year, options = {}) {
  const params = new URLSearchParams();
  if (options.paginated) {
    params.set("paginated", "true");
    params.set("page", String(options.page || 1));
    params.set("page_size", String(options.pageSize || 50));
    if (options.search) params.set("search", options.search);
    if (options.source) params.set("source", options.source);
    if (options.organization) params.set("organization", options.organization);
    if (options.status === "verified") params.set("approved", "true");
    if (options.status === "unverified") params.set("approved", "false");
    if (options.status === "clash") params.set("clash", "true");
    if (options.status === "repeated") params.set("repeated", "true");
    if (options.status === "shared") params.set("shared", "true");
    if (options.status === "rejected") params.set("rejected", "true");
    if (options.minAmount !== "") params.set("min_amount", options.minAmount);
    if (options.maxAmount !== "") params.set("max_amount", options.maxAmount);
    if (options.sort) params.set("sort", options.sort);
  } else {
    params.set("limit", "2000");
  }
  if (range === "all") {
    return `/donations?${params.toString()}`;
  }
  if (range === "day") {
    params.set("date", day);
  } else if (range === "week") {
    params.set("period", "week");
  } else if (range === "year") {
    const y = String(year || istYear());
    params.set("start", `${y}-01-01`);
    params.set("end", `${y}-12-31`);
  } else {
    params.set("month", month);
  }
  return `/donations?${params.toString()}`;
}

function compareDonations(a, b, sort) {
  const dateA = String(a.donation_date || a.created_at || "");
  const dateB = String(b.donation_date || b.created_at || "");
  if (sort === "date_asc") return dateA.localeCompare(dateB);
  if (sort === "amount_desc") return Number(b.amount || 0) - Number(a.amount || 0);
  if (sort === "amount_asc") return Number(a.amount || 0) - Number(b.amount || 0);
  if (sort === "name_asc") return String(a.donor_name || "").localeCompare(String(b.donor_name || ""));
  if (sort === "clash_first") return Number(b.clash || 0) - Number(a.clash || 0) || dateB.localeCompare(dateA);
  if (sort === "status") return Number(a.approved || 0) - Number(b.approved || 0) || Number(b.clash || 0) - Number(a.clash || 0);
  return dateB.localeCompare(dateA);
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div className={`card-warm card-warm-hover p-5 ${accent ? "bg-[#4A5D4E] text-white" : ""}`}>
      <div className={`text-overline ${accent ? "!text-white/70" : ""}`}>{label}</div>
      <div className={`text-2xl mt-2 ${accent ? "text-white" : "text-[#2D312E]"}`} style={{ fontFamily: "Manrope" }}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${accent ? "text-white/70" : "text-[#8A938D]"}`}>{sub}</div>}
    </div>
  );
}
function SmallStat({ label, value, sub, danger, accent }) {
  return (
    <div className={`card-warm p-4 ${accent?"bg-[#4A5D4E] text-white":""}`}>
      <div className={`text-[10px] uppercase tracking-widest ${accent?"text-white/70":danger?"text-[#C05746]":"text-[#8A938D]"} font-bold`}>{label}</div>
      <div className={`text-lg mt-1 ${accent?"text-white":"text-[#2D312E]"}`} style={{ fontFamily: "Manrope" }}>{value}</div>
      {sub && <div className={`text-[10px] ${accent?"text-white/70":"text-[#8A938D]"}`}>{sub}</div>}
    </div>
  );
}

function LeaveList({ title, empty, items, onDelete }) {
  return (
    <div className="border border-[#EAE6DF] rounded-xl bg-[#FDFBF7] p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-[#2D312E]">{title}</h4>
        <span className="text-xs text-[#8A938D]">{items.length}</span>
      </div>
      <div className="space-y-2">
        {items.length === 0 ? (
          <div className="text-sm text-[#8A938D]">{empty}</div>
        ) : items.map(l => (
          <div key={l.id} className="flex justify-between items-center border border-[#EAE6DF] rounded-lg px-4 py-2 text-sm bg-white">
            <span className="text-[#2D312E]">{l.date} - {l.leave_type === "half_day" ? "Half day" : "Full day"} - {l.reason}</span>
            <div className="flex items-center gap-3">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${l.status==="approved"?"bg-emerald-50 text-emerald-700":l.status==="rejected"?"bg-red-50 text-red-700":"bg-amber-50 text-amber-700"}`}>{l.status}</span>
              {l.status !== "approved" && (
                <button data-testid={`delete-leave-${l.id}`} type="button" onClick={() => onDelete(l)}
                  className="text-[#C05746] hover:underline inline-flex items-center gap-1">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PayrollDetail({ salary }) {
  const att = salary.attendance || {};
  const ib = salary.incentive_breakdown || {};
  const db = salary.deduction_breakdown || {};
  return (
    <div className="scroll-mt-24 card-warm p-5 mb-8" data-testid="employee-payroll-detail">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-overline">Payroll</div>
          <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Current month detail</h3>
        </div>
        <div className="grid grid-cols-2 gap-5 text-right">
          <div>
            <div className="text-xs text-[#8A938D] uppercase tracking-widest">Salary till date</div>
            <div className="text-xl sm:text-2xl font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>{formatINR(salary.salary_till_date)}</div>
          </div>
          <div>
            <div className="text-xs text-[#8A938D] uppercase tracking-widest">Full-month net pay</div>
            <div className="text-xl sm:text-2xl font-medium text-[#4A5D4E]" style={{ fontFamily: "Manrope" }}>{formatINR(salary.total_salary)}</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <PayrollCard title="Attendance">
          <PayrollRow k="Present" v={att.present ?? 0} />
          <PayrollRow k="Late" v={att.late ?? 0} />
          <PayrollRow k="Half-day" v={att.half_day ?? 0} />
          <PayrollRow k="Absent" v={att.absent ?? 0} />
          <PayrollRow k="Hours worked" v={`${salary.hours_worked ?? 0} h`} />
        </PayrollCard>
        <PayrollCard title={`Incentives${ib.manual_only ? " (manual)" : ""}`}>
          {ib.manual_only ? (
            <PayrollRow k="Manual" v={formatINR(ib.manual || 0)} />
          ) : (
            <>
              <PayrollRow k="Daily" v={formatINR(ib.daily || 0)} />
              <PayrollRow k="Hourly" v={formatINR(ib.hourly || 0)} />
              <PayrollRow k="Weekly" v={formatINR(ib.weekly || 0)} />
              <PayrollRow k="Monthly" v={formatINR(ib.monthly || 0)} />
              <PayrollRow k="Manual" v={formatINR(ib.manual || 0)} />
            </>
          )}
          <PayrollRow k="Total incentive" v={formatINR(ib.total || salary.monthly_incentive || 0)} strong />
        </PayrollCard>
        <PayrollCard title="Salary">
          <PayrollRow k="Basic" v={formatINR(salary.basic_salary || 0)} />
          <PayrollRow k="Daily wage" v={formatINR(salary.daily_wage || 0)} />
          <PayrollRow k={`Absent (${(db.absent || {}).count ?? 0})`} v={"- " + formatINR((db.absent || {}).total ?? 0)} />
          <PayrollRow k={`Half-day (${(db.half_day || {}).count ?? 0})`} v={"- " + formatINR((db.half_day || {}).total ?? 0)} />
          <PayrollRow k={`Late (${(db.late || {}).count ?? 0})`} v={"- " + formatINR((db.late || {}).total ?? 0)} />
          <PayrollRow k={`Accrued basic (${salary.elapsed_calendar_days || 0} calendar days)`} v={formatINR(salary.accrued_basic_salary || 0)} />
          <PayrollRow k="Salary till date" v={formatINR(salary.salary_till_date)} />
          <PayrollRow k="Full-month net pay" v={formatINR(salary.total_salary)} strong />
          <div className="text-[10px] text-[#8A938D] mt-1">Salary till date = daily wage × elapsed calendar days − applicable deductions.</div>
        </PayrollCard>
      </div>
    </div>
  );
}
function PayrollCard({ title, children }) {
  return (
    <div className="border border-[#EAE6DF] rounded-xl p-3 bg-white">
      <div className="text-[10px] uppercase tracking-widest text-[#8A938D] font-bold mb-2">{title}</div>
      {children}
    </div>
  );
}
function PayrollRow({ k, v, strong }) {
  return (
    <div className={`flex justify-between gap-3 text-xs py-0.5 ${strong ? "border-t border-[#EAE6DF] pt-1 mt-1 font-semibold text-[#2D312E]" : "text-[#5C635E]"}`}>
      <span>{k}</span>
      <span>{v}</span>
    </div>
  );
}
function BreakdownPill({ label, value, testid }) {
  return (
    <div className="border border-[#EAE6DF] rounded-xl p-3 bg-[#FDFBF7]" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#8A938D] font-bold">{label}</div>
      <div className="text-base mt-1 text-[#2D312E]" style={{ fontFamily: "Manrope" }}>{value}</div>
    </div>
  );
}
function IncentiveRules({ incentives }) {
  const daily = incentives?.daily_tiers || [];
  const weekly = incentives?.weekly_tiers || [];
  const monthly = incentives?.monthly_tiers || [];
  const hourly = incentives?.hourly_rules || [];
  const standalone = incentives?.rule_incentives || [];
  if (!daily.length && !weekly.length && !monthly.length && !hourly.length && !standalone.length) {
    return <div className="text-sm text-[#8A938D] mt-3">No active incentive slabs for today.</div>;
  }
  const byType = (type) => standalone.filter((rule) => rule.type === type);
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3 text-sm">
      <RuleGroup title="Daily" rules={[...daily.map(t => formatIncentiveTier(t, "collection")), ...formatStandaloneRules(byType("daily"), "collection")]} />
      <RuleGroup title="Weekly" note={formatWeeklyRange(incentives)} rules={[...weekly.map(t => formatIncentiveTier(t, "week")), ...formatStandaloneRules(byType("weekly"), "week")]} />
      <RuleGroup title="Monthly" rules={[...monthly.map(t => formatIncentiveTier(t, "month")), ...formatStandaloneRules(byType("monthly"), "month")]} />
      <RuleGroup title="Hourly" rules={[
        ...hourly.flatMap(r => (r.slabs || []).map(t => `${r.start_time}-${r.end_time}: ${formatIncentiveTier(t, "donation").replace("If", "if")}`)),
        ...formatStandaloneRules(byType("hourly"), "donation"),
      ]} />
    </div>
  );
}

function formatStandaloneRules(rules, basis) {
  return rules.flatMap((rule) => (rule.tiers || []).map((tier) => {
    const period = formatRulePeriod(rule);
    const prefix = rule.backdated_only ? "Backdated only: " : "";
    return `${prefix}${rule.name || "Incentive"} (${period}): ${formatIncentiveTier(tier, basis).replace(/^Backdated only: /, "").replace(/^If /, "if ")}`;
  }));
}

function formatRulePeriod(rule) {
  if (rule.type === "monthly") return rule.month || "";
  if (rule.type === "weekly") return `${formatDate(rule.start_date)} to ${formatDate(rule.end_date)}`;
  if (rule.type === "hourly") return `${formatDate(rule.date)} ${rule.start_time || ""}-${rule.end_time || ""}`;
  return formatDate(rule.date);
}

function formatIncentiveTier(tier, basis) {
  const payout = tier.unit === "percent" ? `${tier.amount}% of slab amount` : formatINR(tier.amount);
  const prefix = tier.backdated_only ? "Backdated only: " : "";
  if (tier.basis === "target_percent") {
    return `${prefix}If reaches ${tier.min_target_percent ?? tier.min_amount ?? 0}% of monthly target, get ${payout}`;
  }
  return `${prefix}If ${basis} reaches ${formatINR(tier.min_amount)}, get ${payout}`;
}

function formatWeeklyRange(incentives) {
  if (!incentives?.weekly_start_date && !incentives?.weekly_end_date) return "";
  if (incentives?.weekly_start_date && incentives?.weekly_end_date) {
    return `${formatDate(incentives.weekly_start_date)} to ${formatDate(incentives.weekly_end_date)}`;
  }
  return incentives?.weekly_start_date ? `From ${formatDate(incentives.weekly_start_date)}` : `Until ${formatDate(incentives.weekly_end_date)}`;
}

function RuleGroup({ title, note, rules }) {
  return (
    <div className="border border-[#EAE6DF] rounded-xl bg-[#FDFBF7] p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#8A938D] font-bold mb-2">{title}</div>
      {note && <div className="text-xs text-[#8A938D] mb-1">{note}</div>}
      {rules.length === 0 ? (
        <div className="text-xs text-[#8A938D]">Not active</div>
      ) : rules.map((r, i) => <div key={i} className={`text-xs py-0.5 ${String(r).startsWith("Backdated only:") ? "text-[#C05746]" : "text-[#5C635E]"}`}>{r}</div>)}
    </div>
  );
}
function F({ label, value, onChange, placeholder, type = "text", inputMode, testid }) {
  const isDate = type === "date";
  return (
    <div className="min-w-0">
      <label className="text-sm font-medium text-[#5C635E] mb-1.5 block">{label}</label>
      <input data-testid={testid} type={type} inputMode={inputMode} value={value} onWheel={type === "number" ? (e) => e.currentTarget.blur() : undefined} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        style={isDate ? { minWidth: 0, maxWidth: "100%", boxSizing: "border-box" } : undefined}
        className={`block w-full min-w-0 max-w-full h-[46px] overflow-hidden bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5D4E]/20 focus:border-[#4A5D4E] ${isDate ? "appearance-none" : ""}`} />
    </div>
  );
}
function FSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="min-w-0">
      <label className="text-sm font-medium text-[#5C635E] mb-1.5 block">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="block w-full min-w-0 max-w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-4 py-2.5 uppercase">
        {options.map(o => typeof o === "string"
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
function DonationTable({ items, editing, setEditing, onSave, readOnly, highlightStale, sources = [], orgs = [], groupClashes = false }) {
  if (items.length === 0) return <div className="text-sm text-[#8A938D] text-center py-8 border border-dashed border-[#EAE6DF] rounded-xl">No donations.</div>;
  const clashItems = groupClashes ? items.filter((d) => d.clash) : [];
  const normalItems = groupClashes ? items.filter((d) => !d.clash) : items;
  const clashGroups = groupClashes ? groupClashedDonations(clashItems) : [];
  return (
    <div className="space-y-4">
      {clashGroups.length > 0 && (
        <div className="space-y-3" data-testid="clashed-donation-groups">
          <div>
            <div className="text-overline text-[#C05746]">Clashes</div>
            <div className="text-sm text-[#8A938D]">Grouped by donor phone, date and amount for faster checking.</div>
          </div>
          {clashGroups.map((group) => (
            <div key={group.key} className="border border-red-200 bg-red-50/40 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b border-red-100">
                <div>
                  <div className="font-medium text-[#C05746]">{group.title}</div>
                  <div className="text-xs text-[#8A938D]">{group.items.length} clashed donation{group.items.length === 1 ? "" : "s"}</div>
                </div>
                <div className="text-sm font-semibold text-[#4A5D4E]">{formatINR(group.total)}</div>
              </div>
              <DonationRowsTable items={group.items} editing={editing} setEditing={setEditing} onSave={onSave} readOnly={readOnly} highlightStale={highlightStale} sources={sources} orgs={orgs} compact />
            </div>
          ))}
        </div>
      )}
      {normalItems.length > 0 && (
        <DonationRowsTable items={normalItems} editing={editing} setEditing={setEditing} onSave={onSave} readOnly={readOnly} highlightStale={highlightStale} sources={sources} orgs={orgs} />
      )}
    </div>
  );
}

function groupClashedDonations(items) {
  const groups = new Map();
  items.forEach((d) => {
    const date = String(d.donation_date || d.created_at || "").slice(0, 10);
    const key = [d.donor_phone || "", date, Number(d.amount || 0)].join("|");
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        title: `${d.donor_name || "Unknown donor"} | ${d.donor_phone || "No phone"} | ${formatDate(date)} | ${formatINR(d.amount)}`,
        total: 0,
        items: [],
      });
    }
    const group = groups.get(key);
    group.total += Number(d.amount || 0);
    group.items.push(d);
  });
  return Array.from(groups.values()).sort((a, b) => String(a.donor_name || "").localeCompare(String(b.donor_name || "")));
}

function DonationRowsTable({ items, editing, setEditing, onSave, readOnly, highlightStale, sources = [], orgs = [], compact = false }) {
  return (
    <>
    <div className="md:hidden space-y-3">
      {items.map((d) => {
        const isEditing = editing?.id === d.id;
        return <article key={d.id} data-testid={`mobile-row-${d.id}`} className="rounded-2xl border border-[#EAE6DF] bg-white p-4 shadow-sm">
          {isEditing ? <div className="space-y-3">
            <input aria-label="Donor name" className="w-full border border-[#EAE6DF] rounded-lg px-3 py-2" value={editing.donor_name} onChange={(e) => setEditing({ ...editing, donor_name: e.target.value })} />
            <input aria-label="Donor phone" className="w-full border border-[#EAE6DF] rounded-lg px-3 py-2" value={editing.donor_phone} onChange={(e) => setEditing({ ...editing, donor_phone: e.target.value })} />
            <div className="grid grid-cols-2 gap-2"><select aria-label="Organization" className="border border-[#EAE6DF] rounded-lg px-3 py-2 uppercase" value={editing.organization} onChange={(e) => setEditing({ ...editing, organization: e.target.value })}>{(orgs.length ? orgs : ORGS).map((o) => <option key={o} value={o}>{o}</option>)}</select><select aria-label="Source" className="border border-[#EAE6DF] rounded-lg px-3 py-2 uppercase" value={editing.source} onChange={(e) => setEditing({ ...editing, source: e.target.value })}>{(sources.length ? sources : DEFAULT_SOURCES).map((o) => <option key={o} value={o}>{o}</option>)}</select></div>
            <div className="grid grid-cols-2 gap-2"><input aria-label="Amount" type="number" className="border border-[#EAE6DF] rounded-lg px-3 py-2" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} /><input aria-label="Donation date" type="date" className="border border-[#EAE6DF] rounded-lg px-3 py-2" value={(editing.donation_date || "").slice(0, 10)} onChange={(e) => setEditing({ ...editing, donation_date: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2"><button type="button" onClick={onSave} className="min-h-11 rounded-xl bg-[#4A5D4E] text-white">Save</button><button type="button" onClick={() => setEditing(null)} className="min-h-11 rounded-xl border border-[#EAE6DF] text-[#5C635E]">Cancel</button></div>
          </div> : <>
            <div className="flex items-start justify-between gap-3"><div className="min-w-0"><h4 className="font-semibold text-[#2D312E] truncate">{d.donor_name}</h4><a href={`tel:${d.donor_phone}`} className="text-sm text-[#4A5D4E]">{d.donor_phone}</a></div><div className="font-semibold text-[#4A5D4E] shrink-0">{formatINR(d.amount)}</div></div>
            <div className="flex flex-wrap gap-1.5 mt-3"><span className="text-[10px] px-2 py-1 rounded-full bg-[#F4F1EA] uppercase text-[#5C635E]">{d.organization || "No org"}</span><span className="text-[10px] px-2 py-1 rounded-full bg-[#F4F1EA] uppercase text-[#5C635E]">{d.source || "No source"}</span><span className={`text-[10px] px-2 py-1 rounded-full border ${d.approved ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{d.approved ? "Verified" : "Unverified"}</span>{d.clash && <span className="text-[10px] px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">Clash</span>}</div>
            <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-[#EAE6DF]"><span className="text-xs text-[#8A938D]">{formatDate(d.donation_date || d.created_at)}</span>{!readOnly && <button type="button" onClick={() => setEditing({ ...d })} className="min-h-10 px-4 rounded-xl border border-[#EAE6DF] text-sm text-[#4A5D4E]">Edit</button>}</div>
          </>}
        </article>;
      })}
    </div>
    <div className="hidden md:block overflow-x-auto">
      <table className={`w-full text-sm ${compact ? "bg-white/70" : ""}`}>
        <thead>
          <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
            <th className="text-left p-3">Donor</th><th className="text-left p-3">Org</th><th className="text-left p-3">Source</th>
            <th className="text-right p-3">Amount</th><th className="text-left p-3">Status</th><th className="text-left p-3">Date</th>
            {!readOnly && <th className="text-right p-3">Actions</th>}
          </tr>
        </thead>
        <tbody>
        {items.map(d => {
          const staleDays = highlightStale ? Math.floor((Date.now() - new Date(d.created_at).getTime()) / 86400000) : 0;
          const stale = staleDays > 90;
          return (
            <tr key={d.id} data-testid={`row-${d.id}`} className={`border-t border-[#EAE6DF] ${stale ? "bg-red-50/40" : ""}`}>
              <td className="p-3">
                {editing?.id===d.id ? (
                  <><input className="border rounded px-2 py-1 mb-1 w-full" value={editing.donor_name} onChange={e=>setEditing({...editing,donor_name:e.target.value})}/>
                  <input className="border rounded px-2 py-1 w-full" value={editing.donor_phone} onChange={e=>setEditing({...editing,donor_phone:e.target.value})}/></>
                ) : (<>
                  <div className={`font-medium ${stale?"text-[#C05746]":"text-[#2D312E]"}`}>{d.donor_name}</div>
                  <div className="text-xs text-[#8A938D]">{d.donor_phone}</div>
                  {d.shared_group_id && (
                    <div className="text-[10px] text-[#4A5D4E] mt-1">Shared with {d.shared_with_employee_name} from {formatINR(d.shared_total_amount)}</div>
                  )}
                  {d.repeated_donation && (
                    <div className="text-[10px] text-[#8A938D] mt-1">Repeated donation</div>
                  )}
                </>)}
              </td>
              <td className="p-3 uppercase text-[#5C635E]">
                {editing?.id===d.id ? (
                  <select className="border rounded px-2 py-1 w-full bg-white uppercase" value={editing.organization} onChange={e=>setEditing({...editing,organization:e.target.value})}>
                    {(orgs.length ? orgs : ORGS).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : d.organization}
              </td>
              <td className="p-3 uppercase text-[#5C635E]">
                {editing?.id===d.id ? (
                  <select className="border rounded px-2 py-1 w-full bg-white uppercase" value={editing.source} onChange={e=>setEditing({...editing,source:e.target.value})}>
                    {(sources.length ? sources : DEFAULT_SOURCES).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : d.source}
              </td>
              <td className="p-3 text-right font-semibold text-[#4A5D4E]">
                {editing?.id===d.id ? <input type="number" className="border rounded px-2 py-1 w-24 text-right" value={editing.amount} onChange={e=>setEditing({...editing,amount:e.target.value})}/> : formatINR(d.amount)}
              </td>
              <td className="p-3">
                <div className="flex gap-1 flex-wrap">
                  {d.approved && d.verified_by === "sheet" ? (
                    <span data-testid={`verified-sheet-${d.id}`} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" title={d.verified_source ? `From ${d.verified_source}` : "Verified via Google Sheet"}>
                      ✓ Sheet{d.verified_source ? ` · ${d.verified_source}` : ""}
                    </span>
                  ) : d.approved ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Verified</span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Unverified</span>
                  )}
                  {d.clash && <span data-testid="clash-badge" className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300 animate-pulse">⚠ Clash</span>}
                </div>
              </td>
              <td className="p-3 text-xs text-[#5C635E]">
                {editing?.id === d.id ? (
                  <input data-testid={`edit-date-${d.id}`} type="date" className="border rounded px-2 py-1 w-full"
                    value={(editing.donation_date || "").slice(0, 10)}
                    onChange={e => setEditing({ ...editing, donation_date: e.target.value })} />
                ) : (
                  <>
                    <div>{formatDate(d.donation_date || d.created_at)}</div>
                    {d.donation_date && d.donation_date.slice(0, 10) !== (d.created_at || "").slice(0, 10) && (
                      <div className="text-[10px] text-[#8A938D]">added {formatDate(d.created_at)}</div>
                    )}
                  </>
                )}
              </td>
              {!readOnly && (
                <td className="p-3 text-right">
                  {editing?.id===d.id ? (
                    <><button data-testid={`save-${d.id}`} onClick={onSave} className="text-emerald-700 mr-2"><Save className="h-4 w-4 inline"/></button>
                    <button onClick={()=>setEditing(null)} className="text-[#8A938D]"><X className="h-4 w-4 inline"/></button></>
                  ) : <button data-testid={`edit-${d.id}`} onClick={()=>setEditing({...d})} className="text-[#5C635E] hover:text-[#4A5D4E]"><Pencil className="h-4 w-4 inline"/></button>}
                </td>
              )}
            </tr>
          );
        })}
        </tbody>
      </table>
    </div>
    </>
  );
}

function DuplicateDonationModal({ duplicate, amount, date, type, saving, onCancel, onConfirm }) {
  const isCrossEmployee = type === "cross_employee";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-4" data-testid="duplicate-donation-modal">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-[#EAE6DF] overflow-hidden">
        <div className="p-5 border-b border-[#EAE6DF]">
          <div className="text-overline text-[#C05746]">{isCrossEmployee ? "Clash warning" : "Duplicate entry"}</div>
          <h3 className="text-xl font-medium text-[#2D312E] mt-1" style={{ fontFamily: "Manrope" }}>
            {isCrossEmployee ? "Already added by another employee" : "Donation already exists"}
          </h3>
        </div>
        <div className="p-5 space-y-3 text-sm text-[#5C635E]">
          <p>
            {isCrossEmployee
              ? "The same phone number, amount and date is already added by another employee. If you continue, this donation will be saved and marked as clash."
              : "The same phone number, amount and date already exists. Save this only if the donor donated again."}
          </p>
          <div className="rounded-xl border border-[#EAE6DF] bg-[#FDFBF7] p-4 space-y-1">
            <div><span className="text-[#8A938D]">Donor:</span> <span className="font-medium text-[#2D312E]">{duplicate?.donor_name || "This donor"}</span></div>
            <div><span className="text-[#8A938D]">Phone:</span> <span className="font-medium text-[#2D312E]">{duplicate?.donor_phone || "-"}</span></div>
            {isCrossEmployee && (
              <div><span className="text-[#8A938D]">Added by:</span> <span className="font-medium text-[#2D312E]">{duplicate?.employee_name || "Another employee"}</span></div>
            )}
            <div><span className="text-[#8A938D]">Date:</span> <span className="font-medium text-[#2D312E]">{formatDate(date)}</span></div>
            <div><span className="text-[#8A938D]">Amount:</span> <span className="font-medium text-[#2D312E]">{formatINR(amount)}</span></div>
          </div>
        </div>
        <div className="p-4 bg-[#FDFBF7] border-t border-[#EAE6DF] flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={saving}
            className="border border-[#EAE6DF] text-[#5C635E] rounded-xl px-4 py-2 text-sm disabled:opacity-60">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={saving}
            className="bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white rounded-xl px-4 py-2 text-sm disabled:opacity-60">
            {saving ? "Saving..." : isCrossEmployee ? "Save as clash" : "Save as repeated"}
          </button>
        </div>
      </div>
    </div>
  );
}

function normalizePhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.slice(-10);
}

function duplicateDonationDetail(error) {
  const detail = error?.response?.data?.detail;
  if (["duplicate_donation", "cross_employee_duplicate"].includes(detail?.code)) return detail;
  if (typeof detail === "string") {
    try {
      const parsed = JSON.parse(detail);
      if (["duplicate_donation", "cross_employee_duplicate"].includes(parsed?.code)) return parsed;
    } catch {
      return null;
    }
  }
  return null;
}
